import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@environment';
import { EcondosQuery } from '@api/model/query';
import * as qs from 'qs';
import { Log } from '@api/model/log';
import { map } from 'rxjs/operators';

/**
 * Parâmetros de habilitação de logs internos.
 */
export interface EnableInternalLogsQuery {
  level: string;
  http: boolean;
  ttl: number;
}

/**
 * Valores aplicados pelo backend após habilitar logs.
 */
export interface EnableInternalLogsApplied {
  logLevel: string;
  isHttpAccessLogEnabled: boolean;
  isAppLogEnabled: boolean;
}

/**
 * Escopo de aplicação da configuração de logs.
 */
export interface EnableInternalLogsScope {
  environment: string;
  service: string;
}

/**
 * Resposta do endpoint de habilitação de logs.
 */
export interface EnableInternalLogsResponse {
  ok: boolean;
  changed: boolean;
  rejectedFields: string[];
  applied: EnableInternalLogsApplied;
  ttlSeconds: number;
  providedFields: string[];
  scope: EnableInternalLogsScope;
}

@Injectable({ providedIn: 'root' })
export class LogsService {
  private endPoint = `${environment.backendUrl}logs/`;
  private enableLogsEndPoint = `${environment.backendUrl}internal/logs`;

  constructor(private http: HttpClient) {}

  public get(query: EcondosQuery) {
    const params = new HttpParams({ fromString: qs.stringify(query) });
    return this.http.get<Log[]>(this.endPoint, { params });
  }

  public getHardwareLogs(query: EcondosQuery) {
    const params = new HttpParams({ fromString: qs.stringify(query) });
    const options = {
      headers: new HttpHeaders(),
      params,
      observe: 'response' as 'body'
    };
    return this.http.get<any>(`${this.endPoint}hardwares`, options).pipe(
      map(({ body: data, headers }) => {
        return {
          hardwareLogs: data,
          count: headers.get('count')
        };
      })
    );
  }

  public getHardwareErrorLogs(query: EcondosQuery) {
    const params = new HttpParams({ fromString: qs.stringify(query) });
    const options = {
      headers: new HttpHeaders(),
      params,
      observe: 'response' as 'body'
    };
    return this.http.get<any>(`${this.endPoint}hardware-errors`, options).pipe(
      map(({ body: data, headers }) => {
        return {
          hardwareLogs: data,
          count: headers.get('count')
        };
      })
    );
  }

  public getHardwareLogsCollectionList() {
    return this.http.get<string[]>(`${this.endPoint}hardwares/collectionList`);
  }

  /**
   * Habilita logs internos no backend com parâmetros de debug.
   *
   * @param query Configurações para habilitação de logs
   * @returns Observable com o status e os campos aplicados no backend
   * @example
   * logsService.enableInternalLogs({ level: 'debug', http: true, ttl: 500 });
   * @throws Erro HTTP quando a API recusa a operação
   */
  public enableInternalLogs(query: EnableInternalLogsQuery) {
    const params = new HttpParams({ fromString: qs.stringify(query) });
    return this.http.get<EnableInternalLogsResponse>(this.enableLogsEndPoint, { params });
  }
}
